// page-meeting-save.ts — Enregistrement de la fiche réunion.
//
// Extrait de `renderMeetingPage` le 2026-09-08 (point 16). Validation et
// payload viennent du domaine (meeting-form) ; ici les deux confirmations
// (réunion issue d'un calendrier déplacée, occurrence récurrente dont le
// rattachement change) et la navigation après succès.
//
// Globaux ambiants : t, toast, api, confirmStackModal.

import { navigateTo } from '../router.js';
import { _getRaciIds } from './component-action-modal/raci-picker.js';
import {
  buildMeetingPayload,
  isMeetingSaveValid,
  type MeetingPageState,
} from '../domain/meeting-form.js';

/** Champs de la réunion enregistrée que l'enregistrement compare. */
export interface SavableMeeting {
  id: number;
  program_id: number | null;
  project_id: number | null;
  structure_id: number | null;
  committee_id: number;
  meeting_date: string | null;
  from_calendar?: boolean;
  recurring?: boolean;
}

/** RACI depuis les pastilles si l'onglet Participants est rendu, sinon l'état. */
function raciIds(field: string, fallback: number[]): number[] {
  return document.getElementById(`chips-${field}`) ? _getRaciIds(field) : fallback;
}

/** Le comité, le projet, le programme ou la structure a changé. */
export function scopeChanged(meeting: SavableMeeting, state: MeetingPageState): boolean {
  return (
    state.committeeId !== (meeting.committee_id ?? 0) ||
    state.projectId !== (meeting.project_id ?? 0) ||
    state.programId !== (meeting.program_id ?? 0) ||
    state.structureId !== (meeting.structure_id ?? 0)
  );
}

/**
 * Enregistre. Édition : PUT puis retour à la liste. Création : POST puis
 * bascule sur l'URL d'édition, qui déverrouille les onglets dépendants de
 * l'id. Toute confirmation refusée abandonne sans toast.
 */
export async function saveMeeting(
  meeting: SavableMeeting | null,
  state: MeetingPageState,
): Promise<void> {
  if (!isMeetingSaveValid(state)) {
    toast(t('common.required_fields'), 'error');
    return;
  }
  const payload = buildMeetingPayload({
    ...state,
    animatorIds: raciIds('mp-animators', state.animatorIds),
    participantIds: raciIds('mp-participants', state.participantIds),
  });
  // Réunion issue d'un calendrier : changer la date ici ne se répercute ni
  // sur l'agenda d'origine ni chez les invités.
  if (meeting?.from_calendar && meeting.meeting_date !== state.meetingDate) {
    const ok = await confirmStackModal({
      message: t('pageMeetingsRecords.moveCalendarConfirm'),
      confirmLabel: t('pageMeetingsRecords.moveCalendarBtn'),
    });
    if (!ok) return;
  }
  // Occurrence récurrente dont le rattachement change : proposer de propager
  // aux occurrences suivantes. Les deux CTA enregistrent.
  let propagate = false;
  if (meeting?.recurring && scopeChanged(meeting, state)) {
    propagate = await confirmStackModal({
      message: t('pageMeeting.recurrencePropagate.message'),
      confirmLabel: t('pageMeeting.recurrencePropagate.btnYes'),
      cancelLabel: t('pageMeeting.recurrencePropagate.btnNo'),
    });
  }
  try {
    if (meeting) {
      await api.updateMeetingRecord(meeting.id, { ...payload, propagate_to_series: propagate });
      toast(t('common.saved'), 'ok');
      navigateTo('meetings-records', {});
      return;
    }
    const created = (await api.createMeetingRecord(payload)) as { id?: number } | null;
    toast(t('common.saved'), 'ok');
    if (created?.id) {
      window.location.hash = `#meeting/${created.id}?tab=${state.activeTab}`;
    } else {
      navigateTo('meetings-records', {});
    }
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
  }
}
